import { useState } from "react";
import type { LoroDoc } from "loro-crdt";
import { useTranslation } from "react-i18next";
import { assigneesOf, rotationOf, turn } from "../core/circle";
import { setProp } from "../core/ops";
import type { Task } from "../core/schema";
import type { Member } from "../circle/useCircle";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "../ui";

/**
 * *Assigned to* in a shared space (spec §9.29): who a task is for, and — for a repeating task — whether they take
 * turns. One ordered list: `assignee` holds the people in their order; with *Take turns* on, `rec.rotation` is the
 * same list and the pills are numbered and can be dragged to change the order. Switching *Take turns* off keeps the
 * order in `assignee`, so switching it on again brings it back (§9.19). The checkbox is always there; it is greyed
 * out for a task that does not repeat.
 */
export function AssignEditor({
  task,
  members,
  me,
  nameOf,
  repeats,
  ro,
  write,
}: {
  task: Task;
  members: Member[];
  me: string | null;
  nameOf(id: string): string;
  repeats: boolean;
  ro: boolean;
  write(fn: (doc: LoroDoc) => void): void;
}) {
  const { t } = useTranslation();
  const people = members.filter((m) => m.kind !== "viewer");
  const ids = people.map((m) => m.id);
  const rot = rotationOf(task);
  const assigned = assigneesOf(task);
  const turnsOn = repeats && rot.length > 0;
  // the order: the rotation's when it runs, else the assignee list's (older tasks may only have a rotation)
  const order = turnsOn
    ? [...rot, ...assigned.filter((x) => !rot.includes(x))]
    : [...assigned, ...rot.filter((x) => !assigned.includes(x))];
  const listed = order.filter((x) => ids.includes(x));
  const others = people.filter((m) => !listed.includes(m.id));
  const [dragging, setDragging] = useState<string | null>(null);

  const save = (next: string[], turns: boolean) =>
    write((doc) => {
      setProp(doc, task.id, "assignee", next.length ? next : null);
      setProp(doc, task.id, "rec.rotation", turns && next.length ? next : null);
    });
  const toggle = (id: string) =>
    save(listed.includes(id) ? listed.filter((x) => x !== id) : [...listed, id], turnsOn);
  const move = (id: string, to: number) => {
    const rest = listed.filter((x) => x !== id);
    rest.splice(Math.max(0, Math.min(rest.length, to)), 0, id);
    if (rest.join() !== listed.join()) save(rest, turnsOn);
  };
  const whose = turnsOn ? turn(task, ids) : null;
  const pill =
    "h-[26px] rounded-sm border touch:h-11 border-neutral-200 px-2 text-[12.5px] font-medium text-neutral-600 transition hover:bg-neutral-100 aria-pressed:border-accent aria-pressed:bg-accent-tint aria-pressed:text-accent dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-800";

  return (
    <section data-testid="assign">
      <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
        {t("props.assignee")}
      </h3>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("props.assignee")}>
        {listed.map((id, i) => (
          <span key={id} className="inline-flex items-center gap-0.5">
            {/* touch screens: earlier/later buttons, since dragging a pill is awkward with a finger */}
            {turnsOn && !ro && i > 0 && (
              <button
                type="button"
                onClick={() => move(id, i - 1)}
                aria-label={t("assign.earlier", { name: nameOf(id) })}
                className="hidden h-11 w-8 place-items-center rounded-sm text-neutral-500 touch:grid"
                data-testid="assign-earlier"
              >
                <ChevronLeft size={16} aria-hidden />
              </button>
            )}
            <button
              type="button"
              aria-pressed
              disabled={ro}
              draggable={turnsOn && !ro}
              onDragStart={(e) => {
                setDragging(id);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => setDragging(null)}
              onDragOver={(e) => turnsOn && dragging && e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging) move(dragging, i);
                setDragging(null);
              }}
              onKeyDown={(e) => {
                // the keyboard way to reorder: Alt + ← / →
                if (!turnsOn || !e.altKey) return;
                if (e.key === "ArrowLeft") move(id, i - 1);
                if (e.key === "ArrowRight") move(id, i + 1);
              }}
              onClick={() => toggle(id)}
              title={turnsOn ? t("assign.dragHint") : undefined}
              className={`${pill} ${turnsOn ? "cursor-grab" : ""} ${dragging === id ? "opacity-50" : ""}`}
              data-testid="assign-member"
              data-assigned="true"
            >
              {turnsOn ? `${i + 1}. ` : ""}
              {nameOf(id) || people.find((m) => m.id === id)?.name}
            </button>
            {turnsOn && !ro && i < listed.length - 1 && (
              <button
                type="button"
                onClick={() => move(id, i + 1)}
                aria-label={t("assign.later", { name: nameOf(id) })}
                className="hidden h-11 w-8 place-items-center rounded-sm text-neutral-500 touch:grid"
                data-testid="assign-later"
              >
                <ChevronRight size={16} aria-hidden />
              </button>
            )}
          </span>
        ))}
        {others.map((m) => (
          <button
            key={m.id}
            type="button"
            aria-pressed={false}
            disabled={ro}
            onClick={() => toggle(m.id)}
            className={pill}
            data-testid="assign-member"
            data-assigned="false"
          >
            {m.name}
          </button>
        ))}
      </div>
      <label
        className={`mt-2 flex w-fit items-center gap-2 text-[13px] ${repeats ? "" : "text-neutral-400"}`}
        title={repeats ? undefined : t("assign.turnsOnlyRepeating")}
        data-testid="assign-turns"
      >
        <Checkbox
          checked={turnsOn}
          disabled={ro || !repeats || listed.length === 0}
          label={t("assign.takeTurns")}
          onChange={(v) => save(listed, v)}
        />
        {t("assign.takeTurns")}
      </label>
      {(turnsOn || listed.length === 0) && (
        <p className="mt-1 text-[12px] text-neutral-500" data-testid="panel-turn">
          {turnsOn && whose
            ? whose === me
              ? t("circle.yourTurn")
              : t("circle.turn", { name: nameOf(whose) })
            : t("circle.anyone")}
        </p>
      )}
    </section>
  );
}
